<template>
	<div class="better-section flex flex-col px-6 py-10 lg:overflow-hidden">
		<div class="grid w-full max-w-screen-2xl grid-cols-1 gap-x-12 gap-y-16 self-center lg:grid-cols-12">
			<div class="col-span-1 flex flex-col items-center justify-center max-lg:order-2 lg:col-span-6">
				<div class="better-content flex max-w-[600px] flex-col">
					<span class="better-subtitle mb-2.5 text-xl text-[#382C5C] md:text-2xl">
						{{ $t("what_make_us_better_subtitle") }}
					</span>
					<span class="better-title text-3xl font-black leading-normal text-[#382C5C] md:text-4xl">
						<I18nT scope="global" keypath="what_make_us_better_title">
							<template #hr>
								<span class="text-primary">
									{{ $t("human_resource") }}
								</span>
							</template>
						</I18nT>
					</span>
					<span class="better-description mt-5 text-sm text-[#686F7A]">
						{{ $t("what_make_us_better_description") }}
					</span>
					<div class="mt-6 grid grid-cols-2 gap-x-12 gap-y-6">
						<div
							v-for="(item, i) in items"
							:key="i"
							class="feature-item group flex flex-col transition-transform duration-300 hover:translate-x-2"
						>
							<div
								class="feature-icon h-[52px] w-[52px] bg-primary transition-all duration-300 group-hover:scale-110"
								:style="{
									maskImage: `url('${item.image}')`,
									maskSize: '100%',
									maskRepeat: 'no-repeat'
								}"
							></div>
							<span
								class="mt-2.5 text-sm font-bold text-[#382C5C] transition-colors duration-300 group-hover:text-primary"
							>
								{{ item.title }}
							</span>
							<span class="mt-2.5 text-sm text-[#686F7A]">
								{{ item.description }}
							</span>
						</div>
					</div>
				</div>
			</div>
			<div class="col-span-1 flex flex-col items-center justify-center lg:col-span-6 lg:items-end">
				<div class="better-images relative h-auto w-full max-w-[600px] lg:max-w-[620px]">
					<img
						src="/img/landing_what_make_us_better_bg.png"
						alt=""
						class="better-bg-image h-auto w-full object-contain transition-transform duration-500"
					/>
					<img
						src="/img/landing_what_make_us_better.png"
						alt=""
						class="better-main-image absolute left-0 top-0 h-full w-full scale-[0.91] object-contain transition-transform duration-500"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from "@vueuse/core"

const app = useNuxtApp()

const items = computed(() => [
	{
		title: app.$i18n.t("landing_what_make_us_better_1_title"),
		description: app.$i18n.t("landing_what_make_us_better_1_description"),
		image: "/img/landing_what_make_us_better_1.png"
	},
	{
		title: app.$i18n.t("landing_what_make_us_better_2_title"),
		description: app.$i18n.t("landing_what_make_us_better_2_description"),
		image: "/img/landing_what_make_us_better_2.png"
	},
	{
		title: app.$i18n.t("landing_what_make_us_better_3_title"),
		description: app.$i18n.t("landing_what_make_us_better_3_description"),
		image: "/img/landing_what_make_us_better_3.png"
	},
	{
		title: app.$i18n.t("landing_what_make_us_better_4_title"),
		description: app.$i18n.t("landing_what_make_us_better_4_description"),
		image: "/img/landing_what_make_us_better_4.png"
	}
])

onMounted(() => {
	const elements = document.querySelectorAll<HTMLElement>(".better-content, .better-images, .feature-item")

	elements.forEach((el) => {
		useIntersectionObserver(
			el,
			([{ isIntersecting }]) => {
				if (isIntersecting) {
					el.classList.add("animate-in")
				}
			},
			{
				threshold: 0.1,
				rootMargin: "0px 0px -50px 0px"
			}
		)
	})
})
</script>

<style scoped>
/* Scroll animations */
.better-content,
.better-images {
	opacity: 0;
	transform: translateY(40px);
	transition:
		opacity 0.8s ease-out,
		transform 0.8s ease-out;
}

.better-content.animate-in {
	opacity: 1;
	transform: translateY(0);
}

.better-images.animate-in {
	opacity: 1;
	transform: translateY(0);
	transition-delay: 0.2s;
}

/* Title animations */
.better-subtitle {
	animation: fadeInUp 0.8s ease-out both;
}

.better-title {
	animation: fadeInUp 0.8s ease-out 0.1s both;
}

.better-description {
	animation: fadeInUp 0.8s ease-out 0.2s both;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Feature items stagger animation */
.feature-item {
	opacity: 0;
	transform: translateX(-20px);
	transition:
		opacity 0.6s ease-out,
		transform 0.6s ease-out;
}

.feature-item.animate-in {
	opacity: 1;
	transform: translateX(0);
}

.feature-item:nth-child(1) {
	transition-delay: 0.3s;
}

.feature-item:nth-child(2) {
	transition-delay: 0.4s;
}

.feature-item:nth-child(3) {
	transition-delay: 0.5s;
}

.feature-item:nth-child(4) {
	transition-delay: 0.6s;
}

/* Image hover effect */
.better-section:hover .better-bg-image {
	transform: scale(1.05);
}

.better-section:hover .better-main-image {
	transform: scale(0.96) rotate(2deg);
}
</style>
